import translate from '@translate';
import React, { FC, useEffect, useState, useRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { TransportType } from 'src/types/transport';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { checkPermission, useCheckPermission } from 'src/utils/checkPermission';
import TopBlock from './TopBlock';
import { EditKeysType, EDIT_KEYS, INIT_TAB, STORAGE_KEY } from './constants';
import { addMessage } from 'src/resources/alertMessages/reducer';
import {
    clearTransportDetail,
    createTransportDetailFetch,
    getTransportDetailChangelogFetch,
    getTransportDetailFetch,
    getTransportPackageTypesFetch,
    orderTransportShipmentFetch,
    updateTransportDetailFetch,
    getTransportApiCallsFetch,
    getTransportEventsFetch,
    getTransportPickupDateFetch,
} from 'src/resources/transportDetail/reducer';
import RenderComponents from './RenderComponents';
import {
    getAllowedStatuses,
    getChangesProviderCompaniesTransport,
    getCloneTransport,
    getPaidData,
} from './functions';
import ProviderCompanySelector from 'src/components/ProviderCompanySelector';
import { getProviderCompaniesListFetch } from 'src/resources/providerCompaniesList/reducer';
import { ProviderCompanyType } from 'src/types/providerCompany';
import { isTransportAlreadyOrdered } from 'src/utils/isTransportAlreadyOrdered';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import PaidConfirm from './PaidConfirm';
import { PaidActionOptionType } from 'src/components/PaymentEdit';
import { setStorageData } from 'src/resources/storage/reducer';
import isDeepEqual from 'src/utils/isDeepEqual';
import { samePaths } from 'src/utils/checkPaths';
import { FormErrorsType, OnChangeType } from '../../types/global';
import TransportShipperLabel from './TransportShipperLabel';
import api from '@api';
import { useArchiveMode } from 'src/utils/archiveMode';
import { withArchive } from 'src/resources/withArchive';
import ArchiveBanner from 'src/components/ArchiveBanner';
import {
    mergeDefaultSenderIntoTransport,
    parseDefaultSenderPayload,
} from '../OrderDetailContainer/orderToManualTransportPrefill';

export type OnChangeMultipleType = (transportPart: TransportType) => void;

type TransportLocationState = {
    clonedTransport?: TransportType;
    fromOrder?: {
        orderId: number;
        prefill: Partial<TransportType>;
    };
};

const initData: TransportType = { PACKAGES: [], PAID: 0, TYPE: 'expedition' };
const TransportDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const { idTransport } = useParams();

    const newTransport = !idTransport;
    const archive = useArchiveMode();

    const formRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.transportDetail.data);
    const changelog = useSelector((state: RootStoreType) => state.transportDetail.changelog);
    const packageTypeCodes = useSelector((state: RootStoreType) => state.transportDetail.packageTypeCodes);
    const apiCalls = useSelector((state: RootStoreType) => state.transportDetail.apiCalls);
    const events = useSelector((state: RootStoreType) => state.transportDetail.events);
    const loading = useSelector((state: RootStoreType) => state.transportDetail.loading);
    const orderShipmentLoading = useSelector((state: RootStoreType) => state.transportDetail.orderShipmentLoading);
    const transportStatusCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.transportStatusCodes);
    const transportStatusCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.transportStatusCodes
    );
    const transportTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.transportTypeCodes);
    const transportTypeCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.transportTypeCodes
    );
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const currencyCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.currencyCodes);
    const paymentCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.paymentCodes);
    const paymentCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.paymentCodes);
    const shipperCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.shipperCodes);
    const shipperCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.shipperCodes);
    const serviceCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.serviceCodes);
    const serviceCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.serviceCodes);
    const providerServiceCodes = useSelector((state: RootStoreType) => state.user.serviceCodes);
    const lineStateCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.lineStateCodes);
    const boolNumberCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.boolNumberCodes);
    const skladonUser = useSelector((state: RootStoreType) => state.user.data?.SKLADON_USER);
    const locationHistory = useSelector((state: RootStoreType) => state.location.history);
    const providerCompaniesData = useSelector((state: RootStoreType) => state.providerCompaniesList.data);
    const storageData = useSelector((state: RootStoreType) => state.storage.data[STORAGE_KEY]);

    const [editData, setEditData] = useState<TransportType>(getEditData(initData || {}, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [activeTab, setActiveTab] = useState<string>(INIT_TAB);
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);
    const [showShipperLabel, setShowShipperLabel] = useState(false);
    const sourceOrderIdRef = useRef<number | undefined>(undefined);

    const permissionCreate = useCheckPermission('TRANSPORT.CREATE');
    const permissionUpdate = useCheckPermission('TRANSPORT.UPDATE');
    const permissionOrderShipment = useCheckPermission('TRANSPORT.ORDER_SHIPMENT');
    const permissionEditAll = useCheckPermission('TRANSPORT.EDITALL');
    const permissionEditStatus = data?.STATUS && checkPermission(`TRANSPORT.STATUS.EDIT.${data.STATUS.toUpperCase()}`);

    const disableEdit =
        archive ||
        !permissionEditAll && (newTransport ? !permissionCreate : !permissionEditStatus || !permissionUpdate);
    const allowedStatuses = getAllowedStatuses(transportStatusCodes, transportStatusCodesObject, data);

    const backUrl = withArchive('/transports/', archive);

    useEffect(() => {
        if (idTransport) {
            dispatch(setLocationParams({ name: translate('transportDetail.title', { idTransport }), backUrl }));
        } else {
            dispatch(setLocationParams({ name: translate('transportDetail.titleNew'), backUrl }));
        }
    }, [idTransport, backUrl, locationHistory]);

    useEffect(() => {
        if (idTransport) {
            dispatch(getTransportDetailFetch(idTransport));
            return () => {
                dispatch(clearTransportDetail());
            };
        }

        let cancelled = false;
        (async () => {
            try {
                const [nextRes, senderRes] = await Promise.all([
                    api<{ data?: { ESHOP_TRANSPORT_ID?: string } }>('get', 'api/secure/transports/next-eshop-id'),
                    api<{ data?: unknown }>('get', 'api/secure/transports/default-sender'),
                ]);
                const nextId = nextRes.data?.data?.ESHOP_TRANSPORT_ID;
                const sender = parseDefaultSenderPayload(senderRes.data?.data);
                if (cancelled) {
                    return;
                }
                setEditData((prev) => {
                    const withId =
                        nextId && !prev.ESHOP_TRANSPORT_ID ? { ...prev, ESHOP_TRANSPORT_ID: nextId } : prev;
                    return sender ? mergeDefaultSenderIntoTransport(withId, sender) : withId;
                });
            } catch {
                // leave empty — create validation will surface missing id / sender
            }
        })();
        return () => {
            cancelled = true;
            dispatch(clearTransportDetail());
        };
    }, [idTransport, archive]);

    useEffect(() => {
        // Re-fetch when shipper changes OR when leaving/entering detail (clone → /new clears
        // packageTypeCodes via clearTransportDetail while SHIPPER often stays the same).
        if (archive) {
            return;
        }
        dispatch(getTransportPackageTypesFetch(editData.SHIPPER || undefined));
    }, [editData.SHIPPER, idTransport, archive]);

    useEffect(() => {
        if (data) {
            setEditData(data);
            setEditErrors({});
            setActiveTab(INIT_TAB);

            // redirect after create
            if (data.ID !== Number(idTransport)) {
                navigate('/transports/' + String(data.ID));
                setThisStorageData(null);
            }
        }
    }, [data]);

    useEffect(() => {
        if (!changelog && activeTab === 'history') {
            dispatch(getTransportDetailChangelogFetch(idTransport));
        }
        if (!apiCalls && activeTab === 'apiCalls' && idTransport) {
            dispatch(getTransportApiCallsFetch(idTransport));
        }
        if (!events && activeTab === 'events' && idTransport) {
            dispatch(getTransportEventsFetch(idTransport));
        }
    }, [activeTab, changelog, apiCalls, events, idTransport]);

    // save edit data -start
    const editDataRef = useRef<TransportType>({});
    useEffect(() => {
        const locationState = location.state as TransportLocationState | null;
        if (newTransport && locationState?.fromOrder) {
            const { orderId, prefill } = locationState.fromOrder;
            sourceOrderIdRef.current = orderId;
            setEditData((previous) =>
                mergeDefaultSenderIntoTransport(
                    {
                        ...initData,
                        ...prefill,
                        ESHOP_TRANSPORT_ID: prefill.ESHOP_TRANSPORT_ID ?? previous.ESHOP_TRANSPORT_ID,
                        TYPE: prefill.TYPE || 'expedition',
                        PACKAGES: prefill.PACKAGES ?? initData.PACKAGES,
                    },
                    previous
                )
            );
            setEditErrors({});
            setActiveTab(INIT_TAB);
        } else if (newTransport && locationState?.clonedTransport) {
            setEditData({ ...locationState.clonedTransport, TYPE: locationState.clonedTransport.TYPE || 'expedition' });
            setEditErrors({});
            setActiveTab(INIT_TAB);
            (async () => {
                try {
                    const res = await api('get', 'api/secure/transports/next-eshop-id');
                    const nextId = res?.data?.data?.ESHOP_TRANSPORT_ID as string | undefined;
                    if (nextId) {
                        setEditData((prev) => ({ ...prev, ESHOP_TRANSPORT_ID: nextId }));
                    }
                } catch {
                    // ignore
                }
            })();
            return () => {
                if (!editDataRef.current?.ID) {
                    if (isDeepEqual(editDataRef.current, initData)) {
                        setThisStorageData(null);
                    } else {
                        setThisStorageData(editDataRef.current);
                    }
                }
            };
        }
        if (
            newTransport &&
            storageData &&
            // from receivings new or misclick and back 1 url
            (samePaths(locationHistory[0]?.pathname, '/transports') ||
                samePaths(locationHistory[1]?.pathname, '/transports/new'))
        ) {
            setConfirmParams({
                title: translate('transportDetail.confirmGetNewDataStorage'),
                content: translate('transportDetail.confirmGetNewDataStorageText'),
                action: () => {
                    setEditData({ ...storageData, TYPE: storageData.TYPE || 'expedition' });
                },
                onCancel: () => {
                    setThisStorageData(null);
                },
            });
        }
        return () => {
            if (!editDataRef.current?.ID) {
                if (isDeepEqual(editDataRef.current, initData)) {
                    setThisStorageData(null);
                } else {
                    setThisStorageData(editDataRef.current);
                }
            }
        };
    }, []);

    useEffect(() => {
        editDataRef.current = editData;
    }, [editData]);

    const setThisStorageData = (data: TransportType | null) => {
        dispatch(setStorageData({ key: STORAGE_KEY, data }));
    };

    const onChangeMultiple: OnChangeMultipleType = (transportPart) => {
        const newData = { ...editData, ...transportPart };
        const newErrors = { ...editErrors };
        Object.keys(transportPart).forEach((key) => {
            delete newErrors[key];
        });
        setEditData(newData);
        setEditErrors(newErrors);
    };

    // load provider companies
    useEffect(() => {
        if (!providerCompaniesData) {
            dispatch(getProviderCompaniesListFetch());
        }
    }, [providerCompaniesData]);

    const onSetProviderCompany = (type: 'recipient' | 'sender') => {
        setConfirmParams({
            title: translate(`transportDetail.selectProviderCompany${type}`),
            customContent: (confirmData: ProviderCompanyType | null, setConfirmData: React.Dispatch<unknown>) => (
                <ProviderCompanySelector
                    data={confirmData}
                    setData={setConfirmData}
                    providerCompaniesData={providerCompaniesData || []}
                />
            ),
            action: (confirmData?: ProviderCompanyType) => {
                if (!confirmData || !Object.keys(confirmData).length) {
                    return;
                }
                onChangeMultiple(getChangesProviderCompaniesTransport(confirmData, type));
            },
        });
    };

    const onChange: OnChangeType<EditKeysType> = (key, value, error, errorKey) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        if (errorKey) {
            newErrors[errorKey] = error;
        } else {
            newErrors[key] = error;
        }
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const requestPickupDate = (shipper: string) => {
        dispatch(
            getTransportPickupDateFetch({
                shipper,
                callback: (pickupDate) => {
                    if (pickupDate) {
                        setEditData((prev) => ({ ...prev, PICK_UP_AT: pickupDate }));
                        setEditErrors((prev) => {
                            if (!prev.PICK_UP_AT) {
                                return prev;
                            }
                            const next = { ...prev };
                            delete next.PICK_UP_AT;
                            return next;
                        });
                    }
                },
            })
        );
    };

    const pickupPrefillRequestedForShipper = useRef<string | null>(null);
    useEffect(() => {
        if (disableEdit) {
            return;
        }
        if (editData.SHIPPER !== 'GEIS') {
            pickupPrefillRequestedForShipper.current = null;
            return;
        }
        if (editData.PICK_UP_AT) {
            return;
        }
        if (pickupPrefillRequestedForShipper.current === 'GEIS') {
            return;
        }
        pickupPrefillRequestedForShipper.current = 'GEIS';
        requestPickupDate('GEIS');
        // Prefill planned pickup when Geis is selected and date is empty.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disableEdit, editData.SHIPPER, editData.PICK_UP_AT]);

    const handleChangeTab = (newTab: string, requireValidation?: boolean) => {
        if (requireValidation) {
            const newFormErrors = checkFormValidity(formRef);
            if (!Object.keys(newFormErrors).length) {
                setActiveTab(newTab);
            } else {
                dispatch(
                    addMessage({
                        type: 'warning',
                        title: translate('messages.formWarningTitle'),
                        text: translate('messages.formWarningText'),
                    })
                );
            }
            setEditErrors(newFormErrors);
        } else {
            setActiveTab(newTab);
        }
    };

    const handlePaidTransport = (paidOption: PaidActionOptionType) => {
        if (newTransport && paidOption.label !== 'cashOnDelivery') {
            setEditData(getPaidData(paidOption, editData));
        } else {
            setConfirmParams({
                title: translate('transportDetail.confirmPaidAction'),
                customContent: (confirmData: any, setConfirmData: React.Dispatch<any>) => {
                    return (
                        <PaidConfirm
                            data={confirmData}
                            setData={setConfirmData}
                            paidOption={paidOption}
                            currencyCodes={currencyCodes}
                        />
                    );
                },
                action: (confirmData: any) => {
                    if (newTransport) {
                        setEditData(getPaidData(paidOption, editData, confirmData));
                    } else {
                        if (data) {
                            dispatch(
                                updateTransportDetailFetch({
                                    idTransport,
                                    data: getPaidData(paidOption, data, confirmData),
                                })
                            );
                        }
                    }
                },
            });
        }
    };

    const handleSave = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            const formatData = {
                ...editData,
                TYPE: editData.TYPE || 'expedition',
                PACKAGES: editData.PACKAGES?.map((packageItem) => {
                    const newPackage = { ...packageItem };
                    if (newPackage.ID && newPackage.ID < 0) {
                        delete newPackage.ID;
                    }
                    return newPackage;
                }),
            };

            if (newTransport) {
                dispatch(
                    createTransportDetailFetch({
                        transport: formatData,
                        orderId: sourceOrderIdRef.current,
                    })
                );
                sourceOrderIdRef.current = undefined;
            } else {
                dispatch(updateTransportDetailFetch({ idTransport, data: formatData }));
            }
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText'),
                })
            );
        }

        setEditErrors(newFormErrors);
    };

    const handleOrderShipment = () => {
        if (!idTransport) {
            return;
        }
        dispatch(orderTransportShipmentFetch({ id: idTransport, force: isTransportAlreadyOrdered(data) }));
    };

    const handleShowShipperLabel = () => {
        setShowShipperLabel(true);
    };

    const handleDownloadShipperLabel = async () => {
        if (!idTransport) {
            return;
        }
        const response: { data: { data?: { label: string }; label?: string } } = await api(
            'get',
            withArchive(`api/secure/transports/${idTransport}/shipper-label`, archive)
        );
        let base64Data = response.data.data?.label ?? response.data.label;
        if (!base64Data) {
            return;
        }
        if (base64Data.indexOf(';base64,') === -1) {
            base64Data = `data:application/pdf;base64,${base64Data}`;
        }
        const downloadLink = document.createElement('a');
        downloadLink.href = base64Data;
        downloadLink.download = `label-${idTransport}.pdf`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    };

    const handleClone = () => {
        if (!data) {
            return;
        }
        const cloneData = getCloneTransport(data);
        setEditData(cloneData);
        setEditErrors({});
        setActiveTab(INIT_TAB);
        navigate('/transports/new', { state: { clonedTransport: cloneData } });
    };

    return (
        <>
            <TopBlock
                data={data}
                idTransport={idTransport}
                newTransport={newTransport}
                archive={archive}
                disableEdit={disableEdit}
                handleSave={handleSave}
                handleOrderShipment={handleOrderShipment}
                handleClone={handleClone}
                orderShipmentBusy={orderShipmentLoading}
                activeTab={activeTab}
                handleChangeTab={handleChangeTab}
                permissionCreate={permissionCreate}
                permissionOrderShipment={permissionOrderShipment}
            />
            <>
                <ArchiveBanner />
                {loading && <Loading />}
                <RenderComponents
                    data={data}
                    onChange={onChange}
                    onChangeMultiple={onChangeMultiple}
                    onSetProviderCompany={onSetProviderCompany}
                    providerCompaniesData={providerCompaniesData}
                    editData={editData}
                    editErrors={editErrors}
                    newTransport={newTransport}
                    disableEdit={disableEdit}
                    permissionEditAll={permissionEditAll}
                    allowedStatuses={allowedStatuses}
                    transportStatusCodes={transportStatusCodes}
                    boolNumberCodes={boolNumberCodes}
                    currencyCodes={currencyCodes}
                    paymentCodes={paymentCodes}
                    shipperCodes={shipperCodes}
                    serviceCodes={serviceCodes}
                    transportTypeCodes={transportTypeCodes}
                    providerServiceCodes={providerServiceCodes}
                    activeTab={activeTab}
                    handlePaidTransport={handlePaidTransport}
                    formRef={formRef}
                    changelog={changelog}
                    transportStatusCodesObject={transportStatusCodesObject}
                    paymentCodesObject={paymentCodesObject}
                    shipperCodesObject={shipperCodesObject}
                    serviceCodesObject={serviceCodesObject}
                    lineStateCodesObject={lineStateCodesObject}
                    boolNumberCodesObject={boolNumberCodesObject}
                    transportTypeCodesObject={transportTypeCodesObject}
                    skladonUser={skladonUser}
                    handleChangeTab={handleChangeTab}
                    handleSave={handleSave}
                    handleShowShipperLabel={handleShowShipperLabel}
                    handleDownloadShipperLabel={() => {
                        void handleDownloadShipperLabel();
                    }}
                    requestPickupDate={requestPickupDate}
                    packageTypeCodes={packageTypeCodes}
                    apiCalls={apiCalls}
                    events={events}
                    shipper={editData.SHIPPER}
                />
            </>

            {showShipperLabel && idTransport && (
                <TransportShipperLabel
                    idTransport={idTransport}
                    handleHideShipperLabel={() => setShowShipperLabel(false)}
                />
            )}

            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    customContent={confirmParams.customContent}
                    options={confirmParams.options}
                    optionsValue={confirmParams.optionsValue}
                    action={confirmParams.action}
                    onCancel={confirmParams.onCancel}
                    onClose={() => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default TransportDetailContainer;
